import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import { ArtistFormData } from '../../types';
import { useTranslation } from 'react-i18next';
import { ConfirmationModal, useConfirmation } from '../../components/shared';

const ArtistManagement: React.FC = () => {
  const [isAddingArtist, setIsAddingArtist] = useState(false);
  const [editingArtist, setEditingArtist] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [formData, setFormData] = useState<ArtistFormData>({
    first_name: '',
    last_name: '',
    email: '',
    display_name: '',
    bio: '',
    is_artist_exist_on_platforms: false,
    spotify_artist_profile_url: '',
    apple_music_artist_profile_url: ''
  });

  const navigate = useNavigate();
  const { artists, createArtist, updateArtist, deleteArtist } = useData();
  const { t } = useTranslation();
  const { confirmationState, showConfirmation, handleCancel: handleConfirmCancel } = useConfirmation();

  // Sayfalama hesaplamaları
  const totalPages = Math.ceil(artists.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentArtists = artists.slice(startIndex, endIndex);

  // Sayfa değiştiğinde başa dön
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Sayfa başına öğe sayısı değiştiğinde
  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // İlk sayfaya dön
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (editingArtist) {
      updateArtist(editingArtist, formData);
      setEditingArtist(null);
    } else {
      createArtist(formData);
      setIsAddingArtist(false);
    }
    
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      display_name: '',
      bio: '',
      is_artist_exist_on_platforms: false,
      spotify_artist_profile_url: '',
      apple_music_artist_profile_url: ''
    });
  };

  const handleEdit = (artistId: string) => {
    const artist = artists.find(a => a.id === artistId);
    if (artist) {
      setFormData({
        first_name: artist.first_name,
        last_name: artist.last_name,
        email: artist.email,
        display_name: artist.display_name,
        bio: artist.bio || '',
        is_artist_exist_on_platforms: artist.is_artist_exist_on_platforms || false,
        spotify_artist_profile_url: artist.spotify_artist_profile_url || '',
        apple_music_artist_profile_url: artist.apple_music_artist_profile_url || ''
      });
      setEditingArtist(artistId);
      setIsAddingArtist(true);
    }
  };

  const handleViewArtist = (artistId: string) => {
    navigate(`/dashboard/admin/artists/${artistId}`);
  };

  const handleCancel = () => {
    setIsAddingArtist(false);
    setEditingArtist(null);
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      display_name: '',
      bio: '',
      is_artist_exist_on_platforms: false,
      spotify_artist_profile_url: '',
      apple_music_artist_profile_url: ''
    });
  };

  const handleDelete = async (artistId: string) => {
    const artist = artists.find(a => a.id === artistId);
    const artistName = artist ? `${artist.first_name} ${artist.last_name}` : t('admin.thisArtist');
    
    const confirmed = await showConfirmation({
      title: t('admin.deleteArtistTitle'),
      message: t('admin.deleteArtistMessage', { name: artistName }),
      confirmText: t('common.delete'),
      type: 'danger'
    });

    if (confirmed) {
      deleteArtist(artistId);
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-white">{t('admin.artistManagementTitle')}</h1>
          <p className="mt-1 text-sm text-white/70">
            {t('admin.manageAddArtists')}
          </p>
        </div>
        <button
          onClick={() => setIsAddingArtist(true)}
          className="btn-primary flex items-center space-x-2"
          disabled={isAddingArtist}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span>+ {t('admin.addNewArtist')}</span>
        </button>
      </div>

      {/* Add/Edit Artist Form */}
      {isAddingArtist && (
        <div className="glass-card mb-8">
          <h3 className="text-xl font-bold text-white mb-6 flex items-center">
            <svg className="w-6 h-6 mr-3 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            {editingArtist ? t('admin.editArtist') : t('admin.addNewArtist')}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  {t('admin.name')} *
                </label>
                <input
                  type="text"
                  required
                  className="input-field"
                  value={formData.first_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                  placeholder={t('admin.enterName')}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  {t('admin.surname')} *
                </label>
                <input
                  type="text"
                  required
                  className="input-field"
                  value={formData.last_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                  placeholder={t('admin.enterSurname')}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-2">
                {t('admin.email')} *
              </label>
              <input
                type="email"
                required
                className="input-field"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder={t('admin.enterEmail')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-2">
                {t('admin.artistName')} *
              </label>
              <input
                type="text"
                required
                className="input-field"
                value={formData.display_name}
                onChange={(e) => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
                placeholder={t('admin.stageOrGroupName')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-2">
                {t('admin.biography')}
              </label>
              <textarea
                rows={4}
                className="input-field resize-none"
                value={formData.bio}
                onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                placeholder={t('admin.shortBio')}
              />
            </div>

            {/* Platform Hesap Bilgileri */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 space-y-4">
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z" />
                </svg>
                <h4 className="text-sm font-medium text-white">Platform Hesap Bilgileri</h4>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_artist_exist_on_platforms"
                  className="h-4 w-4 text-blue-500 focus:ring-blue-500 border-white/30 rounded bg-white/10"
                  checked={formData.is_artist_exist_on_platforms || false}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_artist_exist_on_platforms: e.target.checked }))}
                />
                <label htmlFor="is_artist_exist_on_platforms" className="ml-2 text-sm text-white">
                  Bu sanatçının Spotify veya Apple Music hesabı var mı?
                </label>
              </div>

              {formData.is_artist_exist_on_platforms && (
                <div className="space-y-4 pl-6 border-l-2 border-blue-500/30">
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Spotify Artist Profile URL
                    </label>
                    <input
                      type="url"
                      className="input-field"
                      value={formData.spotify_artist_profile_url || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, spotify_artist_profile_url: e.target.value }))}
                      placeholder="https://open.spotify.com/artist/..."
                    />
                    <p className="text-xs text-white/50 mt-1">
                      Örn: https://open.spotify.com/artist/3TVXtAsR1Inumwj472S9r4
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Apple Music Artist Profile URL
                    </label>
                    <input
                      type="url"
                      className="input-field"
                      value={formData.apple_music_artist_profile_url || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, apple_music_artist_profile_url: e.target.value }))}
                      placeholder="https://music.apple.com/artist/..."
                    />
                    <p className="text-xs text-white/50 mt-1">
                      Örn: https://music.apple.com/us/artist/taylor-swift/159260351
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-white/20">
              <button
                type="button"
                onClick={handleCancel}
                className="btn-secondary"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                className="btn-primary"
              >
                {editingArtist ? t('common.update') : t('admin.addArtist')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Artists List */}
      <div className="glass-card">
        <h3 className="text-xl font-bold text-white mb-6 flex items-center">
          <svg className="w-6 h-6 mr-3 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
          </svg>
          {t('admin.existingArtists')}
        </h3>
        
        {artists.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-white/40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-white">{t('admin.noArtistsYet')}</h3>
            <p className="mt-1 text-sm text-white/60">{t('admin.addFirstArtist')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-white/20">
                  <th className="px-6 py-4 text-left text-xs font-medium text-white/80 uppercase tracking-wider">
                    {t('admin.artistHeader')}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white/80 uppercase tracking-wider">
                    {t('admin.emailHeader')}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white/80 uppercase tracking-wider">
                    {t('admin.statusHeader')}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white/80 uppercase tracking-wider">
                    {t('admin.registrationDate')}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-white/80 uppercase tracking-wider">
                    {t('admin.actionsHeader')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {currentArtists.map((artist) => (
                  <tr key={artist.id} className="hover:bg-white/5 transition-colors duration-200">
                    <td 
                      className="px-6 py-4 whitespace-nowrap cursor-pointer"
                      onClick={() => handleViewArtist(artist.id)}
                    >
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center">
                          <span className="text-white font-medium text-sm">
                            {artist.display_name[0]?.toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-white hover:text-orange-300 transition-colors duration-200">
                            {artist.display_name}
                          </div>
                          <div className="text-sm text-white/60">{artist.first_name} {artist.last_name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-white">{artist.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-300 border border-green-500/30">
                        {t('admin.active')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white/70">
                      {new Date(artist.created_at).toLocaleDateString('tr-TR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-3">
                        <button
                          onClick={() => handleViewArtist(artist.id)}
                          className="text-orange-300 hover:text-orange-200 transition-colors duration-200"
                        >
                          Detayları Gör
                        </button>
                        <button
                          onClick={() => handleEdit(artist.id)}
                          className="text-blue-300 hover:text-blue-200 transition-colors duration-200"
                        >
                          {t('admin.editAction')}
                        </button>
                        <button
                          onClick={() => handleDelete(artist.id)}
                          className="text-red-300 hover:text-red-200 transition-colors duration-200"
                        >
                          {t('admin.deleteAction')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Sayfalama */}
        {artists.length > 0 && (
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-white/10">
            {/* Sol: Bilgi ve sayfa başına öğe seçimi */}
            <div className="flex items-center gap-4">
              <span className="text-sm text-white/60">
                Toplam {artists.length} sanatçı, {startIndex + 1}-{Math.min(endIndex, artists.length)} arası gösteriliyor
              </span>
              <div className="flex items-center gap-2">
                <span className="text-sm text-white/60">Sayfa başına:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                  className="bg-white/10 border border-white/20 rounded-lg px-2 py-1 text-sm text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>

            {/* Sağ: Sayfa numaraları */}
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                {/* Önceki sayfa */}
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg bg-white/10 border border-white/20 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/20 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                {/* Sayfa numaraları */}
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  // Çok fazla sayfa varsa sadece yakın sayfaları göster
                  if (
                    totalPages <= 7 ||
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`min-w-[36px] h-9 px-3 rounded-lg text-sm font-medium transition-colors ${
                          currentPage === page
                            ? 'bg-orange-500 text-white'
                            : 'bg-white/10 border border-white/20 text-white hover:bg-white/20'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  } else if (
                    (page === currentPage - 2 && currentPage > 3) ||
                    (page === currentPage + 2 && currentPage < totalPages - 2)
                  ) {
                    return (
                      <span key={page} className="text-white/40 px-1">...</span>
                    );
                  }
                  return null;
                })}

                {/* Sonraki sayfa */}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg bg-white/10 border border-white/20 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/20 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationState.isOpen}
        title={confirmationState.title}
        message={confirmationState.message}
        confirmText={confirmationState.confirmText}
        cancelText={confirmationState.cancelText}
        type={confirmationState.type}
        onConfirm={confirmationState.onConfirm}
        onCancel={handleConfirmCancel}
      />
    </div>
  );
};

export default ArtistManagement; 